import _ from 'lodash';
import {useEffect} from 'react';

import {
  type Artifact,
  type ArtifactCollection,
  type ArtifactSequence,
  type FetchJobDetailsQueryHookResult,
  useFetchJobDetailsQuery,
  useFetchJobVersionRunsQuery,
  useFetchJobVersionsByIdQuery,
} from '../../../generated/graphql';
import {type AccessOptions} from '../../../util/permissions';
import {ACTIVE_STATES} from '../constants';
import {getArtifactInfo, getVersionInfo} from '../Jobs/jobUtil';
import {
  convertArtifactToVersion,
  extractRuns,
  findVersionNumber,
} from './utilData';
import {type JobDetails, type JobRun, type JobRuns} from './utilTypes';

const JOB_DETAILS_POLL_INTERVAL_MS = 5000;

type JobDetailsResult =
  | {status: 'idle'; job: null; loading: false; error: undefined}
  | {status: 'loading'; job: null; loading: true; error: undefined}
  | {
      status: 'error';
      job: null;
      loading: false;
      error: NonNullable<FetchJobDetailsQueryHookResult['error']>;
    }
  | {status: 'empty'; job: null; loading: false; error: undefined}
  | {status: 'success'; job: JobDetails; loading: false; error: undefined};

type JobDetailsQueryControls = Pick<
  FetchJobDetailsQueryHookResult,
  'startPolling' | 'stopPolling'
>;

// Get a list of unique top-level config keys from a list of runs.
const getConfigKeys = (runs: JobRuns): string[] => {
  const configKeys: string[] = [];
  for (const run of runs) {
    for (const key in run.config) {
      if (!key.startsWith('_') && !configKeys.includes(key)) {
        configKeys.push(key);
      }
    }
  }
  return configKeys;
};

const toJobDetailsResult = ({
  loading,
  error,
  hasData,
  job,
}: {
  loading: boolean;
  error: FetchJobDetailsQueryHookResult['error'];
  hasData: boolean;
  job: JobDetails | null;
}): JobDetailsResult => {
  if (loading) {
    return {status: 'loading', job: null, loading: true, error: undefined};
  }
  if (error) {
    return {status: 'error', job: null, loading: false, error};
  }
  if (!hasData) {
    return {status: 'idle', job: null, loading: false, error: undefined};
  }
  if (!job) {
    return {status: 'empty', job: null, loading: false, error: undefined};
  }
  return {status: 'success', job, loading: false, error: undefined};
};

/**
 * Job metadata and versions, without any runs. Use this when only the job
 * itself is needed; fetching runs fans out across every job version.
 */
export const useJobVersions = (
  jobId: string,
  defaultJobVersion?: string
): JobDetailsResult => {
  const {data, loading, error} = useFetchJobVersionsByIdQuery({
    variables: {jobId},
    skip: !jobId,
  });
  return toJobDetailsResult({
    loading,
    error,
    hasData: data != null,
    job: getJobDetails(
      data?.artifactCollection as ArtifactSequence | undefined,
      defaultJobVersion
    ),
  });
};

const getRunTargetArtifactId = (job: JobDetails): string | null => {
  const {runTargetNum} = job.versionInfo;
  if (runTargetNum == null) {
    return null;
  }
  return (
    job.versions.find(
      version => findVersionNumber(version.aliases) === runTargetNum
    )?.id ?? null
  );
};

/**
 * Job versions plus the runs (including run args) of only the run target
 * version, which is all the launch drawer can clone from.
 */
export const useJobDrawerDetails = (
  jobId: string,
  {
    defaultJobVersion,
    usedByFilters,
    usedByFirst,
    poll = false,
  }: {
    defaultJobVersion?: string;
    usedByFilters?: string;
    usedByFirst?: number;
    poll?: boolean;
  } = {}
): JobDetailsResult => {
  const versionsResult = useJobVersions(jobId, defaultJobVersion);
  const artifactId =
    versionsResult.status === 'success'
      ? getRunTargetArtifactId(versionsResult.job)
      : null;
  const runsQuery = useFetchJobVersionRunsQuery({
    variables: {artifactId: artifactId ?? '', usedByFilters, usedByFirst},
    skip: !artifactId,
    pollInterval: poll ? JOB_DETAILS_POLL_INTERVAL_MS : undefined,
  });

  if (versionsResult.status !== 'success' || !artifactId) {
    return versionsResult;
  }

  // Keep showing the job while switching versions instead of unmounting the
  // drawer form; runs from the previous version are dropped below.
  const runsArtifact =
    runsQuery.data?.artifact ?? runsQuery.previousData?.artifact;
  if (runsQuery.error || runsArtifact == null) {
    return toJobDetailsResult({
      loading: runsQuery.loading,
      error: runsQuery.error,
      hasData: true,
      job: versionsResult.job,
    });
  }

  const runs =
    runsArtifact.id === artifactId
      ? _.orderBy(
          extractRuns(runsArtifact as Artifact),
          ['createdAt'],
          ['desc']
        )
      : [];
  return toJobDetailsResult({
    loading: false,
    error: undefined,
    hasData: true,
    job: {...versionsResult.job, runs, configKeys: getConfigKeys(runs)},
  });
};

export const hasActiveJobRuns = (
  runs: ReadonlyArray<Pick<JobRun, 'state'>>
): boolean => runs.some(run => ACTIVE_STATES.includes(run.state));

/**
 * Every version of a job with all of its runs, for the job page.
 */
export const useJobPageDetails = (
  jobId: string
): JobDetailsResult & JobDetailsQueryControls => {
  const {data, loading, error, startPolling, stopPolling} =
    useFetchJobDetailsQuery({
      variables: {jobId},
      skip: !jobId,
    });
  const result = toJobDetailsResult({
    loading,
    error,
    hasData: data != null,
    job: getJobDetails(
      data?.artifactCollection as ArtifactSequence | undefined
    ),
  });
  const shouldPoll =
    result.status === 'success' && hasActiveJobRuns(result.job.runs);

  useEffect(() => {
    if (shouldPoll) {
      startPolling(JOB_DETAILS_POLL_INTERVAL_MS);
    } else {
      stopPolling();
    }

    return stopPolling;
  }, [jobId, shouldPoll, startPolling, stopPolling]);

  return {...result, startPolling, stopPolling};
};

export const getJobDetails = (
  ac?: ArtifactCollection | ArtifactSequence,
  defaultJobVersion?: string
) => {
  if (ac == null) {
    return null;
  }
  const name = ac.name;
  const description = ac.description ?? '';
  const projectName = ac.project?.name ?? '';
  const entityName = ac.project?.entity?.name ?? '';
  const projectAccess = (ac.project?.access ?? 'PRIVATE') as AccessOptions;
  const createdAt = ac.createdAt.toString();

  const artifactEdges = ac.artifacts?.edges ?? [];
  const artifacts = artifactEdges.map(edge => edge.node as Artifact);

  const versions = artifacts.map(artifact =>
    convertArtifactToVersion(artifact)
  );

  // Get information such as run target.
  const artifactInfo = getArtifactInfo(artifactEdges as any);
  const versionInfo = getVersionInfo(artifactInfo, defaultJobVersion);

  // Aggregate run information from all versions of the job and sort.
  const aggregatedRuns: JobRuns = [];
  artifacts.forEach(artifact => aggregatedRuns.push(...extractRuns(artifact)));
  const runs = _.orderBy(aggregatedRuns, ['createdAt'], ['desc']);
  const configKeys = getConfigKeys(runs);

  const job: JobDetails = {
    id: ac.id,
    name,
    projectName,
    entityName,
    description,
    createdAt,
    projectAccess,
    versions,
    versionInfo,
    runs,
    configKeys,
  };

  return job;
};
